import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AiSalesRecommendationRecord, SalesAiApiService } from './sales-ai-api.service';
import { SalesAiOutputViewComponent } from './sales-ai-output-view.component';

@Component({
  standalone: true,
  selector: 'ain-sales-ai-recommendations',
  imports: [DecimalPipe, AinPageComponent, AinStateComponent, TableModule, ButtonModule, InputTextModule, FormsModule, AinDatePipe, SalesAiOutputViewComponent],
  template: `
    <ain-page [title]="t('sales.ai.recommendations.title')" [description]="t('sales.ai.recommendations.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <details class="filters" open>
            <summary>{{ t('sales.ai.filters.title') }}</summary>
            <div class="filter-grid">
              <label><span>{{ t('sales.ai.fields.entityType') }}</span><input pInputText [(ngModel)]="filterEntityType" /></label>
              <label><span>{{ t('sales.ai.fields.recommendationType') }}</span><input pInputText [(ngModel)]="filterRecommendationType" /></label>
            </div>
          </details>
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
            <button pButton type="button" [label]="t('sales.ai.models.title')" severity="secondary" (click)="openModels()"></button>
            <button pButton type="button" [label]="t('sales.ai.forecast.run')" (click)="runForecast()"></button>
          </div>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15" [selection]="selectedRows()" (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.ai.fields.recommendationType') }}</th>
                <th>{{ t('sales.ai.fields.entityType') }}</th>
                <th>{{ t('sales.ai.fields.score') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('sales.ai.fields.modelVersion') }}</th>
                <th>{{ t('sales.orders.fields.createdAt') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.recommendationType }}</button></td>
                <td>{{ row.entityType }}</td>
                <td>{{ (row.score * 100) | number:'1.0-1' }}%</td>
                <td>{{ row.status }}</td>
                <td>{{ row.modelVersion }}</td>
                <td>{{ row.createdAtUtc | ainDate:'datetime' }}</td>
              </tr>
            </ng-template>
          </p-table>
          @if (forecastOutput()) {
            <section class="forecast-panel card">
              <h3>{{ t('sales.ai.output.forecastTitle') }}</h3>
              <ain-sales-ai-output-view [outputJson]="forecastOutput()" />
            </section>
          }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel{display:grid;gap:10px}.toolbar{display:flex;gap:8px;flex-wrap:wrap}
    .filters{border:1px solid #e5e7eb;border-radius:8px;padding:8px 12px;background:#fff}
    .filter-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:8px}
    label{display:grid;gap:4px;font-size:12px}
    .link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}
    .error{color:#b91c1c}
    .forecast-panel.card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;background:#fff}
    .forecast-panel h3{margin:0 0 8px;font-size:14px}
  `]
})
export class SalesAiRecommendationsComponent implements OnInit {
  private readonly api = inject(SalesAiApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly rows = signal<AiSalesRecommendationRecord[]>([]);
  readonly selectedRows = signal<AiSalesRecommendationRecord[]>([]);
  readonly forecastOutput = signal('');

  filterEntityType = '';
  filterRecommendationType = '';

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.api.listRecommendations({
      entityType: this.filterEntityType.trim() || undefined,
      recommendationType: this.filterRecommendationType.trim() || undefined
    }).subscribe({
      next: items => { this.rows.set(items); this.loading.set(false); },
      error: () => { this.rows.set([]); this.error.set(this.t('sales.ai.recommendations.errorLoad')); this.loading.set(false); }
    });
  }

  onSelectionChange(s: AiSalesRecommendationRecord[] | AiSalesRecommendationRecord | null): void {
    this.selectedRows.set(Array.isArray(s) ? s : s ? [s] : []);
  }

  openDetail(id: string): void { void this.router.navigate(['/sales/ai-recommendations', id]); }
  openModels(): void { void this.router.navigate(['/sales/ai-models']); }

  runForecast(): void {
    this.api.forecast({ forecastType: 'demand', inputSnapshotJson: '{}' }).subscribe({
      next: r => this.forecastOutput.set(r.outputJson),
      error: () => this.error.set(this.t('sales.ai.forecast.error'))
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
